"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebauthnHelpers = void 0;
const tslib_1 = require("tslib");
const AuthenticationFactorStatus_1 = require("../authentication/AuthenticationFactorStatus");
const AuthenticationFactorType_1 = require("../authentication/AuthenticationFactorType");
const AuthenticationStatus_1 = require("../authentication/AuthenticationStatus");
const CredentialOnDevice_1 = require("../authentication/CredentialOnDevice");
const SignedDataConveyance_1 = require("../authentication/SignedDataConveyance");
const EndpointType_1 = require("../endpoint/EndpointType");
const Error_1 = require("../shared/Error");
const IResult_1 = require("../shared/IResult");
const Reason_1 = require("../shared/Reason");
class WebauthnHelpers {
    static convertErrorToResult(error) {
        if (error instanceof Error && error.message === Error_1.CustomerAuthnError.NOT_SUPPORTED) {
            return {
                result: {
                    action: IResult_1.Action.DECLINED,
                    reasons: [Reason_1.Reason.DEVICE_DOESNT_SUPPORT],
                },
                authentication: {
                    status: AuthenticationStatus_1.AuthenticationStatus.NOT_SUPPORTED,
                },
            };
        }
        else if (error.message === Error_1.CustomerAuthnError.REJECTED) {
            return {
                result: {
                    action: IResult_1.Action.DECLINED,
                    reasons: [Reason_1.Reason.AUTHENTICATION_FAILED],
                },
                authentication: {
                    status: AuthenticationStatus_1.AuthenticationStatus.DENIED,
                },
            };
        }
        else {
            return {
                result: {
                    action: IResult_1.Action.DECLINED,
                    reasons: [Reason_1.Reason.AUTHENTICATION_FAILED],
                },
                authentication: {
                    status: AuthenticationStatus_1.AuthenticationStatus.SYSTEM_ERROR,
                },
            };
        }
    }
    static buildWebAuthnRegisterRequest(request) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!request.payload.endpoint.channelDomainName || !request.payload.endpoint.channelName) {
                console.log("WebauthnHelpers no channelDomainName or channelName");
                return Promise.reject(new Error(Error_1.CustomerAuthnError.INTERNAL_ERROR));
            }
            const rp = {
                name: request.payload.endpoint.channelName,
                id: request.payload.endpoint.channelDomainName,
            };
            const user = {
                id: request.payload.customerDigital.digitalCustomerID,
                name: request.payload.customerDigital.username,
                displayName: request.payload.customerDigital.friendlyName,
            };
            if (!request.payload.authentication.customerDataToSign) {
                console.log("WebauthnHelpers no customer data to sign");
                return Promise.reject(new Error(Error_1.CustomerAuthnError.INTERNAL_ERROR));
            }
            const challenge = request.payload.authentication.customerDataToSign;
            const fidoAuthenticator = (_a = request.payload.authentication.factors) === null || _a === void 0 ? void 0 : _a.find(factor => {
                return (factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL ||
                    factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_CROSS_PLATFORM ||
                    factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_PLATFORM);
            });
            if (!fidoAuthenticator || (!fidoAuthenticator.cryptoAlgorithm && !fidoAuthenticator.cryptoAlgorithms)) {
                return Promise.reject(new Error(Error_1.CustomerAuthnError.INTERNAL_ERROR));
            }
            if (fidoAuthenticator.cryptoAlgorithm && !fidoAuthenticator.cryptoAlgorithms) {
                fidoAuthenticator.cryptoAlgorithms = [fidoAuthenticator.cryptoAlgorithm];
            }
            const pubKeyCredParams = [];
            (_b = fidoAuthenticator.cryptoAlgorithms) === null || _b === void 0 ? void 0 : _b.forEach(alg => {
                pubKeyCredParams.push({ type: "public-key", alg });
            });
            const timeout = request.payload.interaction.ttl;
            const attestation = SignedDataConveyance_1.SignedDataConveyance.toLowerCaseEnum(request.payload.authentication.signedDataConveyance);
            const authenticatorSelection = this.buildAuthenticatorSelectionCriteria(request.payload.authentication);
            return { rp, user, challenge, pubKeyCredParams, timeout, attestation, authenticatorSelection };
        });
    }
    static buildWebAuthnAuthRequest(request) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (!request.payload.originEndpoint ||
                !request.payload.originEndpoint.channelDomainName ||
                !request.payload.authentication.customerDataToSign ||
                !request.payload.endpoints) {
                console.log(`buildWebAuthnAuthRequest: ${JSON.stringify(request)}`);
                return Promise.reject(new Error(Error_1.CustomerAuthnError.INTERNAL_ERROR));
            }
            const challenge = request.payload.authentication.customerDataToSign;
            const timeout = request.payload.interaction.ttl ? request.payload.interaction.ttl * 1000 : 10000;
            const rpId = request.payload.originEndpoint.channelDomainName;
            const allowCredentials = [];
            request.payload.endpoints.forEach(endpoint => {
                if (endpoint.instanceID) {
                    allowCredentials.push({ type: "public-key", id: endpoint.instanceID });
                }
            });
            if (allowCredentials.length === 0) {
                return Promise.reject(new Error(Error_1.CustomerAuthnError.INTERNAL_ERROR));
            }
            return { challenge, timeout, rpId, allowCredentials };
        });
    }
    static isPlatformAuthenticationRequest(request) {
        const factors = request.payload.authentication.factors;
        if (!factors)
            return false;
        const factor = factors.find(factor => {
            return factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_PLATFORM;
        });
        if (factor)
            return true;
        else
            return false;
    }
    static convertAuthenticationFactorToEndpointType(factors) {
        const factor = factors.find(factor => {
            return (factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_PLATFORM ||
                factor.type == AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_CROSS_PLATFORM ||
                factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL);
        });
        switch (factor === null || factor === void 0 ? void 0 : factor.type) {
            case AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL: {
                return EndpointType_1.EndpointType.FIDO_AUTHENTICATOR;
            }
            case AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_CROSS_PLATFORM: {
                return EndpointType_1.EndpointType.FIDO_AUTHENTICATOR_CROSS_PLATFORM;
            }
            case AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_PLATFORM: {
                return EndpointType_1.EndpointType.FIDO_AUTHENTICATOR_PLATFORM;
            }
            default: {
                return EndpointType_1.EndpointType.UNKNOWN;
            }
        }
    }
    static buildAuthenticatorSelectionCriteria(authentication) {
        var _a, _b;
        const fidoAuthenticator = (_a = authentication.factors) === null || _a === void 0 ? void 0 : _a.find(factor => {
            return (factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL ||
                factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_CROSS_PLATFORM ||
                factor.type === AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_PLATFORM);
        });
        if (!fidoAuthenticator)
            return;
        const userVerificationFactor = (_b = authentication.factors) === null || _b === void 0 ? void 0 : _b.find(factor => {
            return factor.type === AuthenticationFactorType_1.AuthenticationFactorType.DEVICE_USER_CREDENTIAL;
        });
        const userVerification = this.getUserVerificationFromAuthenticationFactor(userVerificationFactor);
        const residentKey = CredentialOnDevice_1.CredentialOnDevice.toLowerCaseEnum(fidoAuthenticator === null || fidoAuthenticator === void 0 ? void 0 : fidoAuthenticator.credentialOnDevice);
        const authenticatorAttachment = this.authenticationFactorTypeToAuthenticatorAttachment(fidoAuthenticator);
        const authenticatorSelection = {
            residentKey,
            authenticatorAttachment,
            userVerification,
        };
        return authenticatorSelection;
    }
    static authenticationFactorTypeToAuthenticatorAttachment(factor) {
        switch (factor.type) {
            case AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_PLATFORM: {
                return "platform";
            }
            case AuthenticationFactorType_1.AuthenticationFactorType.FIDO_CREDENTIAL_CROSS_PLATFORM: {
                return "cross-platform";
            }
            default: {
                return undefined;
            }
        }
    }
    static getUserVerificationFromAuthenticationFactor(factor) {
        if ((factor === null || factor === void 0 ? void 0 : factor.status) === AuthenticationFactorStatus_1.AuthenticationFactorStatus.REQUIRED) {
            return "required";
        }
        if ((factor === null || factor === void 0 ? void 0 : factor.status) === AuthenticationFactorStatus_1.AuthenticationFactorStatus.PREFERRED) {
            return "preferred";
        }
        else {
            return "discouraged";
        }
    }
}
exports.WebauthnHelpers = WebauthnHelpers;
//# sourceMappingURL=WebauthnHelpers.js.map